import type * as React from "react";
import { useId } from "react";

type Props = {
	checked: boolean;
	onChange: (next: boolean) => void;
	/** Visible text next to the switch; also used as its accessible name. */
	label?: string;
	/** Accessible name when there is no visible label. */
	ariaLabel?: string;
	disabled?: boolean;
};

export function Toggle({
	checked,
	onChange,
	label,
	ariaLabel,
	disabled,
}: Props) {
	const labelId = useId();

	const track: React.CSSProperties = {
		width: 48,
		height: 28,
		flexShrink: 0,
		borderRadius: "var(--radius-pill)",
		padding: 3,
		display: "flex",
		justifyContent: checked ? "flex-end" : "flex-start",
		background: checked ? "var(--accent-500)" : "var(--toggle-track-off)",
		transition: "background 160ms",
		cursor: disabled ? "not-allowed" : "pointer",
		opacity: disabled ? 0.5 : 1,
		border: "none",
	};

	return (
		<span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
			<button
				type="button"
				role="switch"
				aria-checked={checked}
				aria-label={label ? undefined : ariaLabel}
				aria-labelledby={label ? labelId : undefined}
				disabled={disabled}
				onClick={() => onChange(!checked)}
				style={track}
			>
				<span
					style={{
						width: 22,
						height: 22,
						borderRadius: "var(--radius-pill)",
						background: "var(--toggle-knob)",
						boxShadow: "var(--toggle-knob-shadow)",
					}}
				/>
			</button>
			{label && (
				<span
					id={labelId}
					style={{
						fontSize: 13,
						fontWeight: 600,
						color: "var(--content-muted)",
					}}
				>
					{label}
				</span>
			)}
		</span>
	);
}
